// src/components/NicknameSetupModal.jsx
import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { supabase } from '../supabaseClient'
import { TermsModal } from './TermsModal'
import { PrivacyModal } from './PrivacyModal'

export function NicknameSetupModal({ userId, onComplete, onCancel, cancelConfirmMessage = null }) {
  const [nickname, setNickname] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showCancelConfirm, setShowCancelConfirm] = useState(false)

  const trimmed = nickname.trim()
  const isValid = trimmed.length >= 2 && trimmed.length <= 20

  const handleSubmit = async () => {
    if (!isValid || !agreed) return
    setLoading(true)
    setError('')

    // upsert: 既存プロファイルが残っていても確実に上書き（アカウント削除後の再登録に対応）
    const { error: dbError } = await supabase
      .from('profiles')
      .upsert({ user_id: userId, nickname: trimmed }, { onConflict: 'user_id' })

    if (dbError) {
      setError('登録に失敗しました。もう一度お試しください。')
      setLoading(false)
      return
    }

    onComplete(trimmed)
  }

  /*
    ヘッダーに backdropFilter があるため、その中で
    position:fixed を使うとヘッダー内にクリップされる。
    createPortal で document.body 直下に描画することで
    ヘッダーのスタッキングコンテキストを回避する。
  */
  return ReactDOM.createPortal(
    <>
      <div style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9000,
        padding: '16px',
        boxSizing: 'border-box',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: '28px 24px',
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          boxSizing: 'border-box',
          position: 'relative',
          overflow: 'hidden',
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>
            ニックネームを設定
          </h2>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>
            Howtoon内で表示される名前です（2〜20文字）
          </p>

          <div style={{ position: 'relative', marginBottom: 8 }}>
            <input
              type="text"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder="ニックネームを入力"
              maxLength={20}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 48px 12px 14px',
                border: '1.5px solid #e5e7eb',
                borderRadius: 10,
                fontSize: 15,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.15s',
              }}
              onFocus={e => { e.target.style.borderColor = '#9333ea' }}
              onBlur={e => { e.target.style.borderColor = '#e5e7eb' }}
            />
            <span style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              fontSize: 11, color: trimmed.length > 20 ? '#ef4444' : '#9ca3af',
            }}>
              {trimmed.length}/20
            </span>
          </div>

          {error && (
            <p style={{ fontSize: 12, color: '#ef4444', marginBottom: 12 }}>{error}</p>
          )}

          {/* 同意チェックボックス */}
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            marginTop: 16, cursor: 'pointer', userSelect: 'none',
          }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: 2, accentColor: '#9333ea', width: 16, height: 16, flexShrink: 0 }}
            />
            <span style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>
              <button
                type="button"
                onClick={e => { e.preventDefault(); setShowTerms(true) }}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#9333ea', fontWeight: 600, cursor: 'pointer',
                  fontSize: 12, textDecoration: 'underline',
                }}
              >
                利用規約
              </button>
              {' および '}
              <button
                type="button"
                onClick={e => { e.preventDefault(); setShowPrivacy(true) }}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#9333ea', fontWeight: 600, cursor: 'pointer',
                  fontSize: 12, textDecoration: 'underline',
                }}
              >
                プライバシーポリシー
              </button>
              {' に同意します'}
            </span>
          </label>

          {/* キャンセル確認オーバーレイ（cancelConfirmMessage が設定されているとき） */}
          {showCancelConfirm && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.97)',
              borderRadius: 20, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', padding: '24px', zIndex: 1,
            }}>
              <p style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 20, textAlign: 'center', lineHeight: 1.7 }}>
                {cancelConfirmMessage}
              </p>
              <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                <button
                  onClick={() => setShowCancelConfirm(false)}
                  style={{ flex: 1, padding: '11px 0', background: '#f3f4f6', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                >いいえ</button>
                <button
                  onClick={onCancel}
                  style={{ flex: 1, padding: '11px 0', background: '#ef4444', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' }}
                >はい</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              onClick={cancelConfirmMessage ? () => setShowCancelConfirm(true) : onCancel}
              disabled={loading}
              style={{
                flex: 1,
                padding: '11px 0',
                background: '#f3f4f6',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                color: '#6b7280',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              キャンセル
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid || !agreed || loading}
              style={{
                flex: 1,
                padding: '11px 0',
                background: isValid && agreed && !loading
                  ? 'linear-gradient(135deg, #9333ea, #ec4899)'
                  : '#e5e7eb',
                border: 'none',
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                color: isValid && agreed && !loading ? '#fff' : '#9ca3af',
                cursor: isValid && agreed && !loading ? 'pointer' : 'not-allowed',
                transition: 'background 0.2s',
              }}
            >
              {loading ? '登録中...' : '登録する'}
            </button>
          </div>
        </div>
      </div>

      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      {showPrivacy && <PrivacyModal onClose={() => setShowPrivacy(false)} />}
    </>,
    document.body
  )
}
